import React from 'react';
import { Box, Typography } from '@mui/material';
import { SwapHorizOutlined } from '@mui/icons-material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { TradeInCredit, useTradeInCredits } from '../../hooks/useTradeInCredits';
import { tokens } from '../../theme/tokens';

/** "Trade-in credit: $350 when you trade in, through Nov 18, 2026" for the largest live credit. */
export function tradeInCreditText(
  credit: TradeInCredit,
  t: (key: string, options?: Record<string, unknown>) => string,
  formatCurrency: (value: number) => string,
  formatDate: (iso: string) => string
): string {
  const amount = formatCurrency(credit.amount);
  return credit.endsAt
    ? t('store.tradeInCredits.credit', { amount, date: formatDate(credit.endsAt) })
    : t('store.tradeInCredits.creditNoEnd', { amount });
}

/**
 * A sheet's bare end date is stored as that day's UTC midnight and is live
 * through the whole local day, so it is shown as that calendar day (read in
 * UTC); a timed end is shown in the viewer's zone.
 */
export const formatTradeInEndDate = (iso: string): string => {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const bareDate = /T00:00:00(?:\.000)?Z$/.test(iso);
  return new Date(iso).toLocaleDateString(undefined, bareDate ? { ...options, timeZone: 'UTC' } : options);
};

/**
 * A product's live trade-in credit (flag `trade_in_credit_display`, MOT-742),
 * shown beside the price and never changing it. Renders nothing while the
 * flag is off, the store has not turned the display on, or nothing is live.
 * `compact` is the browser card's chip; the default is the product details line.
 */
export const TradeInCreditLine: React.FC<{ productId: string; compact?: boolean }> = (props) => {
  const { enabled } = useOrgFeatureFlag('trade_in_credit_display');
  // Flag off: nothing else is read or asked for.
  return enabled ? <TradeInCreditLineShown {...props} /> : null;
};

const TradeInCreditLineShown: React.FC<{ productId: string; compact?: boolean }> = ({ productId, compact = false }) => {
  const { t } = useAppTranslation();
  const tx = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { formatCurrency } = useLocale();
  const credits = useTradeInCredits(productId, true);
  const credit = credits[0];
  if (!credit) return null;
  const text = tradeInCreditText(credit, tx, formatCurrency, formatTradeInEndDate);

  if (compact) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 20,
          px: 0.875,
          borderRadius: '4px',
          bgcolor: tokens.signal.info.tint,
          border: `1px solid ${tokens.signal.info.border}`,
          color: tokens.signal.info.ink,
          fontSize: '0.6875rem',
          fontWeight: 600,
        }}
      >
        {text}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SwapHorizOutlined fontSize="small" sx={{ color: tokens.signal.info.ink }} />
      <Typography variant="body2" sx={{ color: tokens.signal.info.ink, fontWeight: 600 }}>
        {text}
      </Typography>
    </Box>
  );
};

export default TradeInCreditLine;
